const ALLOWED_NODE_TYPES = new Set(['start', 'end', 'process', 'decision', 'subprocess', 'data']);

const TYPE_TO_SHAPE = {
  start: { shape: 'ellipse', color: '#22c55e', width: 40, height: 40 },
  end: { shape: 'ellipse', color: '#ef4444', width: 40, height: 40 },
  process: { shape: 'rectangle', color: '#3b82f6', width: 90, height: 40 },
  subprocess: { shape: 'predefinedProcess', color: '#60a5fa', width: 90, height: 40 },
  decision: { shape: 'rhombus', color: '#f59e0b', width: 60, height: 50 },
  data: { shape: 'cylinder', color: '#06b6d4', width: 45, height: 50 },
};

export const SEMANTIC_FLOWCHART_SYSTEM_PROMPT = `Sen bir flowchart semantic parser'sın.
Görevin, kullanıcı senaryosunu SADECE anlamsal akış grafına dönüştürmektir.

KURALLAR:
- Sadece geçerli JSON döndür.
- Markdown, açıklama, yorum, code fence yok.
- Çıktı şeması dışına çıkma.
- Düğümler ve bağlantılar mantıksal olarak tam olmalı.
- Karar noktalarında branch koşulları açık olmalı.
- Eksik bilgi varsa makul varsayım yap ve "assumptions" alanına yaz.
- Render/style/geometry/id üretme; bunlar sistem tarafından eklenecek.
- Akışı gereksiz yere parçalama; semantik olarak aynı işi yapan ardışık adımları birleştir.

JSON ŞEMASI:
{
  "workflow_name": "string",
  "assumptions": ["string"],
  "nodes": [
    {
      "key": "N1",
      "type": "start|end|process|decision|subprocess|data",
      "label": "string",
      "description": "string"
    }
  ],
  "edges": [
    {
      "from": "N1",
      "to": "N2",
      "condition": "string",
      "priority": 10
    }
  ]
}

KISITLAR:
1) En az 1 start ve 1 end olmalı.
2) Her edge, var olan node key'lerine bağlanmalı.
3) decision node'undan en az 2 çıkış olmalı (uygunsa).
4) condition boş ise "" ver.
5) priority, aynı source için 10,20,30... olarak artmalı.
6) Döngü varsa edge ile açıkça göster.
7) JSON parse edilebilir olmalı.
8) Çıktıyı kısa tut:
   - label en fazla 80 karakter
   - description en fazla 160 karakter
   - assumptions en fazla 12 madde
   - gereksiz tekrarı kaldır
`;

const stripCodeFences = (text) =>
  safeStr(text)
    .replace(/^```[a-zA-Z0-9_-]*\s*/m, '')
    .replace(/```$/m, '')
    .trim();

const cleanupJsonCandidate = (text) =>
  safeStr(text)
    .replace(/\uFEFF/g, '')
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/,\s*([}\]])/g, '$1')
    .trim();

const extractFirstBalancedObject = (text) => {
  const src = safeStr(text);
  let start = -1;
  let depth = 0;
  let inString = false;
  let escaped = false;

  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];
    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (ch === '\\') {
        escaped = true;
      } else if (ch === '"') {
        inString = false;
      }
      continue;
    }
    if (ch === '"') {
      inString = true;
      continue;
    }
    if (ch === '{') {
      if (start === -1) start = i;
      depth += 1;
      continue;
    }
    if (ch === '}') {
      if (start !== -1) {
        depth -= 1;
        if (depth === 0) {
          return src.slice(start, i + 1);
        }
      }
    }
  }

  // Tam kapanmadıysa, ilk "{" ile son "}" arasında kalan en iyi adayı döndür.
  const first = src.indexOf('{');
  const last = src.lastIndexOf('}');
  if (first >= 0 && last > first) return src.slice(first, last + 1);
  if (first >= 0) return src.slice(first);
  return src;
};

export const buildSemanticFlowchartMessages = (scenarioText) => {
  const scenario = String(scenarioText || '').trim();
  return [
    { role: 'system', content: SEMANTIC_FLOWCHART_SYSTEM_PROMPT },
    { role: 'user', content: `KULLANICI SENARYOSU:\n${scenario}` },
  ];
};

const safeStr = (value, fallback = '') => (value == null ? fallback : String(value));

const to20Hex = (input) => {
  const str = safeStr(input, 'seed');
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < str.length; i += 1) {
    const c = str.charCodeAt(i);
    h1 ^= c;
    h1 = Math.imul(h1, 16777619);
    h2 ^= (c + i) & 0xff;
    h2 = Math.imul(h2, 2246822519);
  }
  const a = (h1 >>> 0).toString(16).padStart(8, '0');
  const b = (h2 >>> 0).toString(16).padStart(8, '0');
  const c = (((h1 ^ h2) >>> 0).toString(16).padStart(8, '0'));
  return (a + b + c).slice(0, 20);
};

const toFiveByFour = (hex20) =>
  `${hex20.slice(0, 4)}-${hex20.slice(4, 8)}-${hex20.slice(8, 12)}-${hex20.slice(12, 16)}-${hex20.slice(16, 20)}`;

const makeStableId = (seed, usedSet) => {
  let attempt = 0;
  // Collision'da deterministik fallback.
  while (attempt < 1000) {
    const hex = to20Hex(`${seed}#${attempt}`);
    const id = toFiveByFour(hex);
    if (!usedSet.has(id)) {
      usedSet.add(id);
      return id;
    }
    attempt += 1;
  }
  throw new Error('Stable id üretilemedi.');
};

export const parseJsonFromAiText = (rawText) => {
  const text = stripCodeFences(rawText);
  if (!text) throw new Error('AI cevabı boş.');

  const attempts = [
    text,
    cleanupJsonCandidate(text),
    extractFirstBalancedObject(text),
    cleanupJsonCandidate(extractFirstBalancedObject(text)),
  ];

  let lastError = null;
  for (let i = 0; i < attempts.length; i += 1) {
    const candidate = attempts[i];
    if (!candidate) continue;
    try {
      return JSON.parse(candidate);
    } catch (err) {
      lastError = err;
    }
  }

  throw new Error(
    `AI cevabı parse edilebilir JSON içermiyor. ${lastError?.message ? `Detay: ${lastError.message}` : ''}`.trim(),
  );
};

const normalizePriorityPerSource = (edges) => {
  const grouped = new Map();
  edges.forEach((edge) => {
    const source = edge.from;
    if (!grouped.has(source)) grouped.set(source, []);
    grouped.get(source).push(edge);
  });

  const normalized = [];
  grouped.forEach((list) => {
    const sorted = [...list].sort((a, b) => {
      const pa = Number.isFinite(Number(a.priority)) ? Number(a.priority) : Number.MAX_SAFE_INTEGER;
      const pb = Number.isFinite(Number(b.priority)) ? Number(b.priority) : Number.MAX_SAFE_INTEGER;
      if (pa !== pb) return pa - pb;
      return safeStr(a.to).localeCompare(safeStr(b.to));
    });
    sorted.forEach((edge, idx) => {
      normalized.push({ ...edge, priority: (idx + 1) * 10 });
    });
  });
  return normalized;
};

export const validateSemanticFlowchartGraph = (input) => {
  const errors = [];
  const warnings = [];

  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return { ok: false, errors: ['Kök JSON obje olmalı.'], warnings, value: null };
  }

  const workflowName = safeStr(input.workflow_name).trim();
  if (!workflowName) errors.push('workflow_name zorunlu.');

  const assumptions = Array.isArray(input.assumptions)
    ? input.assumptions.map((x) => safeStr(x).trim()).filter(Boolean)
    : [];

  const rawNodes = Array.isArray(input.nodes) ? input.nodes : null;
  const rawEdges = Array.isArray(input.edges) ? input.edges : null;
  if (!rawNodes) errors.push('nodes array olmalı.');
  if (!rawEdges) errors.push('edges array olmalı.');
  if (errors.length) return { ok: false, errors, warnings, value: null };

  const seenKeys = new Set();
  const nodes = rawNodes.map((node, index) => {
    const key = safeStr(node?.key).trim();
    const type = safeStr(node?.type).trim().toLowerCase();
    const label = safeStr(node?.label).trim();
    const description = safeStr(node?.description).trim();

    if (!key) errors.push(`nodes[${index}].key zorunlu.`);
    if (key && seenKeys.has(key)) errors.push(`nodes[${index}] key tekrar ediyor: ${key}`);
    if (key) seenKeys.add(key);
    if (!ALLOWED_NODE_TYPES.has(type)) {
      errors.push(`nodes[${index}].type geçersiz: ${type || '(boş)'}`);
    }
    if (!label) errors.push(`nodes[${index}].label zorunlu.`);

    return { key, type, label, description };
  });

  const nodeKeys = new Set(nodes.map((x) => x.key).filter(Boolean));

  const edges = rawEdges.map((edge, index) => {
    const from = safeStr(edge?.from).trim();
    const to = safeStr(edge?.to).trim();
    const condition = safeStr(edge?.condition).trim();
    const rawPriority = Number(edge?.priority);
    const priority = Number.isFinite(rawPriority) ? rawPriority : null;

    if (!from) errors.push(`edges[${index}].from zorunlu.`);
    if (!to) errors.push(`edges[${index}].to zorunlu.`);
    if (from && !nodeKeys.has(from)) errors.push(`edges[${index}].from bilinmiyor: ${from}`);
    if (to && !nodeKeys.has(to)) errors.push(`edges[${index}].to bilinmiyor: ${to}`);
    if (priority != null && (priority <= 0 || priority % 10 !== 0)) {
      warnings.push(`edges[${index}] priority (${priority}) 10'un katı değil; normalize edilecek.`);
    }

    return {
      from,
      to,
      condition,
      priority,
    };
  });

  const startCount = nodes.filter((n) => n.type === 'start').length;
  const endCount = nodes.filter((n) => n.type === 'end').length;
  if (startCount < 1) errors.push('En az bir start node gerekli.');
  if (endCount < 1) errors.push('En az bir end node gerekli.');

  const outgoing = new Map();
  edges.forEach((edge) => {
    if (!outgoing.has(edge.from)) outgoing.set(edge.from, []);
    outgoing.get(edge.from).push(edge);
  });

  nodes.forEach((node) => {
    if (node.type === 'decision') {
      const outs = outgoing.get(node.key) || [];
      if (outs.length < 2) {
        errors.push(`Decision node en az 2 çıkışa sahip olmalı: ${node.key}`);
      }
    }
  });

  const normalizedEdges = normalizePriorityPerSource(edges);

  const value = {
    workflow_name: workflowName,
    assumptions,
    nodes,
    edges: normalizedEdges,
  };

  return {
    ok: errors.length === 0,
    errors,
    warnings,
    value: errors.length ? null : value,
  };
};

export const buildSemanticRepairMessages = ({ rawText, validationErrors, parseError }) => {
  const errors = (validationErrors || []).map((x) => `- ${x}`).join('\n');
  const parseErrText = parseError ? `Parse hatası:\n- ${safeStr(parseError)}\n\n` : '';
  return [
    {
      role: 'system',
      content:
        'Geçersiz JSON çıktısını düzeltme asistanısın. Sadece geçerli JSON döndür. Şema dışına çıkma. Markdown kullanma.',
    },
    {
      role: 'user',
      content: `Aşağıdaki AI çıktısı geçerli JSON değil veya şema validasyonundan geçemedi.\n\n${parseErrText}Şema Hataları:\n${errors || '- (yok)'}\n\nHatalı çıktı:\n${safeStr(rawText)}`,
    },
  ];
};

const snap = (value, grid) => Math.round(value / grid) * grid;

const buildNodeStyle = ({ shape, color }) =>
  `shape=${shape};fillColor=${color};strokeColor=#000000;fontSize=10;fontColor=#000000;whiteSpace=wrap;html=1;overflow=hidden;connectable=1;selectable=1;movable=1;resizable=1;`;

const buildEdgeStyle = () =>
  'endArrow=block;edgeStyle=orthogonalEdgeStyle;rounded=0;orthogonalLoop=1;perimeter=1;bendable=1;editable=1;selectable=1;movable=1;labelBackgroundColor=#fff;labelBorderColor=#e0e0e0;fontColor=#000000;';

const computeLayeredLayout = (nodes, edges) => {
  const outgoing = new Map();
  const incoming = new Map();
  const incomingCount = new Map(nodes.map((n) => [n.key, 0]));
  edges.forEach((edge) => {
    if (!outgoing.has(edge.from)) outgoing.set(edge.from, []);
    outgoing.get(edge.from).push(edge);
    if (!incoming.has(edge.to)) incoming.set(edge.to, []);
    incoming.get(edge.to).push(edge);
    incomingCount.set(edge.to, (incomingCount.get(edge.to) || 0) + 1);
  });

  const starts = nodes.filter((n) => n.type === 'start').map((n) => n.key);
  const queue = starts.length
    ? [...starts]
    : nodes
        .filter((n) => (incomingCount.get(n.key) || 0) === 0)
        .map((n) => n.key);
  if (!queue.length && nodes.length) queue.push(nodes[0].key);

  const layer = new Map();
  queue.forEach((k) => layer.set(k, 0));
  let guard = 0;
  while (queue.length && guard < nodes.length * nodes.length + 10) {
    guard += 1;
    const current = queue.shift();
    const currentLayer = layer.get(current) || 0;
    const nextList = outgoing.get(current) || [];
    nextList.forEach((edge) => {
      const next = edge.to;
      const candidate = currentLayer + 1;
      const prev = layer.get(next);
      if (prev == null || candidate > prev) {
        layer.set(next, candidate);
        queue.push(next);
      }
    });
  }

  // Ulaşılamayan node'ları son katmanlara ekle.
  let maxLayer = Math.max(0, ...Array.from(layer.values()));
  nodes.forEach((node) => {
    if (!layer.has(node.key)) {
      maxLayer += 1;
      layer.set(node.key, maxLayer);
    }
  });

  const grouped = new Map();
  nodes.forEach((node) => {
    const l = layer.get(node.key) || 0;
    if (!grouped.has(l)) grouped.set(l, []);
    grouped.get(l).push(node);
  });

  Array.from(grouped.keys()).forEach((l) => {
    grouped.get(l).sort((a, b) => safeStr(a.key).localeCompare(safeStr(b.key)));
  });

  // Kesişim azaltmak için katman içi sıralamayı ebeveyn/çocuk barycenter ile iyileştir.
  const orderedLayers = new Map();
  Array.from(grouped.keys())
    .sort((a, b) => a - b)
    .forEach((l) => {
      orderedLayers.set(l, [...(grouped.get(l) || [])]);
    });

  const layerKeys = Array.from(orderedLayers.keys()).sort((a, b) => a - b);
  const indexMapForLayer = (items) => {
    const map = new Map();
    items.forEach((item, idx) => map.set(item.key, idx));
    return map;
  };
  const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);

  for (let i = 1; i < layerKeys.length; i += 1) {
    const l = layerKeys[i];
    const prev = layerKeys[i - 1];
    const prevIndex = indexMapForLayer(orderedLayers.get(prev) || []);
    const current = [...(orderedLayers.get(l) || [])];
    current.sort((a, b) => {
      const aParents = (incoming.get(a.key) || [])
        .map((e) => e.from)
        .filter((k) => prevIndex.has(k))
        .map((k) => prevIndex.get(k));
      const bParents = (incoming.get(b.key) || [])
        .map((e) => e.from)
        .filter((k) => prevIndex.has(k))
        .map((k) => prevIndex.get(k));
      const aScore = avg(aParents);
      const bScore = avg(bParents);
      if (aScore == null && bScore == null) return safeStr(a.key).localeCompare(safeStr(b.key));
      if (aScore == null) return 1;
      if (bScore == null) return -1;
      if (aScore !== bScore) return aScore - bScore;
      return safeStr(a.key).localeCompare(safeStr(b.key));
    });
    orderedLayers.set(l, current);
  }

  for (let i = layerKeys.length - 2; i >= 0; i -= 1) {
    const l = layerKeys[i];
    const next = layerKeys[i + 1];
    const nextIndex = indexMapForLayer(orderedLayers.get(next) || []);
    const current = [...(orderedLayers.get(l) || [])];
    current.sort((a, b) => {
      const aChildren = (outgoing.get(a.key) || [])
        .map((e) => e.to)
        .filter((k) => nextIndex.has(k))
        .map((k) => nextIndex.get(k));
      const bChildren = (outgoing.get(b.key) || [])
        .map((e) => e.to)
        .filter((k) => nextIndex.has(k))
        .map((k) => nextIndex.get(k));
      const aScore = avg(aChildren);
      const bScore = avg(bChildren);
      if (aScore == null && bScore == null) return safeStr(a.key).localeCompare(safeStr(b.key));
      if (aScore == null) return 1;
      if (bScore == null) return -1;
      if (aScore !== bScore) return aScore - bScore;
      return safeStr(a.key).localeCompare(safeStr(b.key));
    });
    orderedLayers.set(l, current);
  }

  return {
    layerMap: layer,
    groupedLayers: orderedLayers,
    outgoing,
    incoming,
  };
};

const serializePoints = (points) => JSON.stringify(points.map((p) => ({ x: p.x, y: p.y })));

const computeCompactCoordinates = ({
  groupedLayers,
  grid,
  startX,
  startY,
  layerSpacingX,
  nodeSpacingY,
}) => {
  const sortedLayers = Array.from(groupedLayers.keys()).sort((a, b) => a - b);
  const layers = sortedLayers.map((layerNo) => ({
    layerNo,
    nodes: groupedLayers.get(layerNo) || [],
  }));

  const maxRows = Math.max(1, ...layers.map((l) => l.nodes.length));
  const rowPitch = Math.max(80, nodeSpacingY);
  const minLayerGap = Math.max(70, Math.floor(layerSpacingX * 0.45));
  const layerWidthMap = new Map();

  layers.forEach((layer) => {
    let maxW = 60;
    layer.nodes.forEach((node) => {
      const meta = TYPE_TO_SHAPE[node.type] || TYPE_TO_SHAPE.process;
      maxW = Math.max(maxW, meta.width);
    });
    layerWidthMap.set(layer.layerNo, maxW);
  });

  const layerXMap = new Map();
  let cursorX = startX;
  layers.forEach((layer, idx) => {
    if (idx === 0) {
      layerXMap.set(layer.layerNo, snap(cursorX, grid));
      return;
    }
    const prevLayer = layers[idx - 1];
    const prevW = layerWidthMap.get(prevLayer.layerNo) || 60;
    cursorX += prevW + minLayerGap;
    layerXMap.set(layer.layerNo, snap(cursorX, grid));
  });

  const coordinateByKey = new Map();
  layers.forEach((layer) => {
    const layerNodes = layer.nodes;
    const rowOffset = ((maxRows - layerNodes.length) * rowPitch) / 2;
    layerNodes.forEach((node, rowIndex) => {
      const meta = TYPE_TO_SHAPE[node.type] || TYPE_TO_SHAPE.process;
      const x = layerXMap.get(layer.layerNo) || startX;
      const y = snap(startY + rowOffset + rowIndex * rowPitch, grid);
      coordinateByKey.set(node.key, {
        x,
        y,
        w: meta.width,
        h: meta.height,
        row: rowIndex,
        layer: layer.layerNo,
      });
    });
  });

  return {
    coordinateByKey,
  };
};

const buildEdgeWaypoints = ({
  sourceMeta,
  targetMeta,
  outIndex,
  outCount,
  grid,
  laneIndex = 0,
}) => {
  const sourceX = sourceMeta.x;
  const sourceY = sourceMeta.y;
  const sourceW = sourceMeta.w;
  const sourceH = sourceMeta.h;
  const targetX = targetMeta.x;
  const targetY = targetMeta.y;
  const targetW = targetMeta.w;
  const targetH = targetMeta.h;

  const sourceMidX = sourceX + Math.round(sourceW / 2);
  const sourceMidY = sourceY + Math.round(sourceH / 2);
  const targetMidX = targetX + Math.round(targetW / 2);
  const targetMidY = targetY + Math.round(targetH / 2);
  const sameLayer = sourceMeta.layer === targetMeta.layer;
  const isForward = targetMeta.layer > sourceMeta.layer;
  const spread = 28;
  const branchOffset = snap((outIndex - (outCount - 1) / 2) * spread, grid);
  const laneSpacing = 34;
  const clearance = 34;
  const sidePadding = 30;

  // Aynı source'tan çıkan forward edge'leri ayrı şeritlere dağıt.
  if (isForward && !sameLayer) {
    if (outCount <= 1) return [];
    const sourceExitX = snap(sourceX + sourceW + 26, grid);
    const laneY = snap(sourceMidY + branchOffset, grid);
    return [{ x: sourceExitX, y: laneY }];
  }

  // Aynı katman veya geri dönen edge'ler için global dip yerine yerel koridor kullan.
  const localTopBase = Math.min(sourceY, targetY) - clearance;
  const localBottomBase = Math.max(sourceY + sourceH, targetY + targetH) + clearance;
  const topLaneY = snap(localTopBase - laneIndex * laneSpacing, grid);
  const bottomLaneY = snap(localBottomBase + laneIndex * laneSpacing, grid);

  const topCost = Math.abs(sourceMidY - topLaneY) + Math.abs(targetMidY - topLaneY);
  const bottomCost = Math.abs(sourceMidY - bottomLaneY) + Math.abs(targetMidY - bottomLaneY);
  const laneY = topCost <= bottomCost ? topLaneY : bottomLaneY;

  if (sameLayer) {
    const horizontalDir = targetMidX >= sourceMidX ? 1 : -1;
    const sideX = horizontalDir > 0
      ? snap(sourceX + sourceW + sidePadding, grid)
      : snap(sourceX - sidePadding, grid);
    const targetSideX = horizontalDir > 0
      ? snap(targetX - sidePadding, grid)
      : snap(targetX + targetW + sidePadding, grid);
    const viaY = snap(targetMidY + branchOffset, grid);
    return [
      { x: sideX, y: sourceMidY },
      { x: sideX, y: laneY },
      { x: targetSideX, y: laneY },
      { x: targetSideX, y: viaY },
    ];
  }

  // Back edge: kaynak-hedef aralığının dışından (sol/sağ) kısa koridor.
  const toLeft = targetMidX <= sourceMidX;
  const corridorX = toLeft
    ? snap(Math.min(sourceX, targetX) - sidePadding, grid)
    : snap(Math.max(sourceX + sourceW, targetX + targetW) + sidePadding, grid);
  const viaY = snap(targetMidY + branchOffset, grid);
  return [
    { x: corridorX, y: sourceMidY },
    { x: corridorX, y: laneY },
    { x: corridorX, y: viaY },
  ];
};

export const compileSemanticGraphToMxData = (
  semanticGraph,
  options = {},
) => {
  const {
    workflowId = '100022941',
    workflowName = semanticGraph?.workflow_name || 'AI Flowchart',
    startX = 80,
    startY = 80,
    grid = 20,
    layerSpacingX = 220,
    nodeSpacingY = 110,
  } = options;

  const checked = validateSemanticFlowchartGraph(semanticGraph);
  if (!checked.ok) {
    throw new Error(`Semantic graph geçersiz: ${checked.errors.join(' | ')}`);
  }

  const graph = checked.value;
  const { groupedLayers } = computeLayeredLayout(graph.nodes, graph.edges);
  const usedIds = new Set();
  const keyToId = new Map();
  const nodeMetaByKey = new Map();
  const contents = [];
  const { coordinateByKey } = computeCompactCoordinates({
    groupedLayers,
    grid,
    startX,
    startY,
    layerSpacingX,
    nodeSpacingY,
  });

  Array.from(groupedLayers.keys())
    .sort((a, b) => a - b)
    .forEach((layerNo) => {
      const layerNodes = groupedLayers.get(layerNo) || [];
      layerNodes.forEach((node, rowIndex) => {
        const meta = TYPE_TO_SHAPE[node.type] || TYPE_TO_SHAPE.process;
        const nodeId = makeStableId(`node:${node.key}`, usedIds);
        keyToId.set(node.key, nodeId);

        const coord = coordinateByKey.get(node.key) || {
          x: snap(startX + layerNo * layerSpacingX, grid),
          y: snap(startY + rowIndex * nodeSpacingY, grid),
          layer: layerNo,
          row: rowIndex,
        };
        const x = coord.x;
        const y = coord.y;

        nodeMetaByKey.set(node.key, {
          id: nodeId,
          x,
          y,
          w: meta.width,
          h: meta.height,
          row: coord.row ?? rowIndex,
          layer: coord.layer ?? layerNo,
        });

        contents.push({
          id: nodeId,
          vertex: '1',
          edge: '0',
          definition: node.label,
          GeometryX: x,
          GeometryY: y,
          GeometryWidth: meta.width,
          GeometryHeight: meta.height,
          style: buildNodeStyle(meta),
          kosul: '',
          filename: '',
          parent: String(workflowId),
          include: '',
          script: '',
          ai_prompt: node.description || '',
          GeometryRelative: '0',
          GeometryAs: 'geometry',
          LOEKZ: '',
          LODAT: null,
          sourceHandle: '',
          targetHandle: '',
        });
      });
    });

  const outgoingBySource = new Map();
  graph.edges.forEach((edge) => {
    if (!outgoingBySource.has(edge.from)) outgoingBySource.set(edge.from, []);
    outgoingBySource.get(edge.from).push(edge);
  });
  outgoingBySource.forEach((list) => {
    list.sort((a, b) => Number(a.priority) - Number(b.priority));
  });

  const laneCounterByFamily = new Map();

  graph.edges.forEach((edge, idx) => {
    const sourceId = keyToId.get(edge.from);
    const targetId = keyToId.get(edge.to);
    if (!sourceId || !targetId) return;
    const edgeId = makeStableId(`edge:${edge.from}->${edge.to}:${idx}`, usedIds);
    const sourceMeta = nodeMetaByKey.get(edge.from);
    const targetMeta = nodeMetaByKey.get(edge.to);
    if (!sourceMeta || !targetMeta) return;
    const sourceOut = outgoingBySource.get(edge.from) || [];
    const outIndex = Math.max(0, sourceOut.findIndex((it) => it === edge));
    const outCount = Math.max(1, sourceOut.length);
    const isBackOrSame = (targetMeta.layer || 0) <= (sourceMeta.layer || 0);
    let lane = 0;
    if (isBackOrSame) {
      const family = `${sourceMeta.layer}->${targetMeta.layer}:${targetMeta.layer === sourceMeta.layer ? 'same' : 'back'}`;
      lane = laneCounterByFamily.get(family) || 0;
      laneCounterByFamily.set(family, lane + 1);
    }
    const points = buildEdgeWaypoints({
      sourceMeta,
      targetMeta,
      outIndex,
      outCount,
      grid,
      laneIndex: lane,
    });

    contents.push({
      id: edgeId,
      vertex: '0',
      edge: '1',
      definition: 'Next',
      kosul: String(edge.priority),
      source: sourceId,
      target: targetId,
      GeometryX: 0,
      GeometryY: 0,
      GeometryWidth: 0,
      GeometryHeight: 0,
      style: buildEdgeStyle(),
      GeometryPoints: points.length ? serializePoints(points) : null,
      filename: '',
      parent: String(workflowId),
      include: '',
      script: '',
      ai_prompt: edge.condition || '',
      GeometryRelative: '0',
      GeometryAs: 'geometry',
      LOEKZ: '',
      LODAT: null,
      sourceHandle: '',
      targetHandle: '',
    });
  });

  return {
    workflow: {
      id: String(workflowId),
      name: workflowName,
    },
    AI_GLOBAL: [],
    contents,
  };
};

export const generateSemanticGraphWithAutoRepair = async ({
  callAI,
  scenario,
  model = 'gpt-4o',
  maxTokens = 3200,
  temperature = 0,
}) => {
  if (typeof callAI !== 'function') {
    throw new Error('callAI fonksiyonu zorunlu.');
  }
  const firstMessages = buildSemanticFlowchartMessages(scenario);
  const firstRaw = await callAI({
    model,
    messages: firstMessages,
    max_tokens: maxTokens,
    temperature,
  });
  let parsed;
  let validation;
  let firstParseError = null;
  try {
    parsed = parseJsonFromAiText(firstRaw);
    validation = validateSemanticFlowchartGraph(parsed);
  } catch (err) {
    firstParseError = err?.message || String(err);
    validation = { ok: false, errors: [], warnings: [] };
  }

  if (validation.ok && !firstParseError) {
    return {
      semanticGraph: validation.value,
      repaired: false,
      warnings: validation.warnings,
      raw: firstRaw,
    };
  }

  const repairMessages = buildSemanticRepairMessages({
    rawText: firstRaw,
    validationErrors: validation.errors,
    parseError: firstParseError,
  });
  const repairedRaw = await callAI({
    model,
    messages: repairMessages,
    max_tokens: maxTokens,
    temperature: 0,
  });
  try {
    parsed = parseJsonFromAiText(repairedRaw);
  } catch (err) {
    // Uzun senaryolarda ikinci bir "compact repair" dene.
    const compactRepairMessages = [
      {
        role: 'system',
        content:
          'JSON tamiri yapıyorsun. Sadece geçerli JSON döndür. Cevabı kısa tut: label<=60, description<=120, assumptions<=8.',
      },
      {
        role: 'user',
        content: `Aşağıdaki yanıt parse edilemedi. Geçerli JSON'a dönüştür ve kısalt.\n\nHata:\n${err?.message || String(err)}\n\nYanıt:\n${safeStr(repairedRaw)}`,
      },
    ];
    const compactRaw = await callAI({
      model,
      messages: compactRepairMessages,
      max_tokens: Math.max(1200, Math.floor(maxTokens * 0.7)),
      temperature: 0,
    });
    try {
      parsed = parseJsonFromAiText(compactRaw);
    } catch (compactErr) {
      throw new Error(
        `Repair cevabı da parse edilemedi: ${compactErr?.message || String(compactErr)}`,
      );
    }
  }
  validation = validateSemanticFlowchartGraph(parsed);

  if (!validation.ok) {
    throw new Error(`Repair sonrası da geçersiz çıktı: ${validation.errors.join(' | ')}`);
  }

  return {
    semanticGraph: validation.value,
    repaired: true,
    warnings: validation.warnings,
    raw: repairedRaw,
  };
};
